import React from "react";
import {
  Mic,
  Video,
  Globe,
  Phone,
  Sparkles,
  Stethoscope,
  UtensilsCrossed,
  Dumbbell,
  ShoppingBag,
  Building2,
  Scissors,
  GraduationCap,
  Check,
} from "lucide-react";
import { Box, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import InteractiveWorldMap from "../../components/InteractiveWorldMap/InteractiveWorldMap.jsx";
import "./SmbOnboarding.css";
import Footer from "../../components/navFoot/Footer";
import NavigationBar from "../../components/navFoot/NavigationBar";
import { useNavigate } from "react-router-dom";
import HeroSection from "./HeroSection";
import UseCaseCard from "./UseCaseCard";

export default function SmbOnboarding() {
  const isBelow991 = useMediaQuery("(max-width:991px)");
  const navigate = useNavigate();

  return (
    <>
      <NavigationBar />
      <div className="smb-onboarding-wrapper min-h-screen bg-gradient-to-br from-[#0F0F1E] via-[#1A1B2E] to-[#0F0F1E] text-white overflow-x-hidden w-full">
        <HeroSection />

        {/* Features Section */}
        <section
          id="features"
          className="py-14 md:py-20 px-4 md:px-6 bg-[#1A1B2E]/50"
        >
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-100px", amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-center mb-10 md:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-balance">
                Everything You Need,
                <span className="block text-[#E5B978]">Powered by Voice</span>
              </h2>
              <p className="text-base md:text-xl text-white/70 max-w-full mx-auto">
                One voice command unlocks endless possibilities for your
                business. From creating engaging content to building your
                digital presence and automating customer communications, ModalX
                gives you professional tools that work as fast as you can speak.
                Save hours of work and focus on what matters most—growing your
                business and serving your customers better.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {/* Feature 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, margin: "-50px", amount: 0.3 }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                className="group bg-gradient-to-br from-[#252640] to-[#1A1B2E] p-6 md:p-8 rounded-2xl border border-white/10 hover:border-[#E5B978]/50 transition-all hover:shadow-xl hover:shadow-[#E5B978]/10"
              >
                <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-[#E5B978]/10 flex items-center justify-center mb-5 md:mb-6 group-hover:bg-[#E5B978]/20 transition-colors">
                  <Video className="w-6 md:w-7 h-6 md:h-7 text-[#E5B978]" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-3">
                  Content That Drives Lead
                </h3>
                <p className="text-white/70 mb-5 md:mb-6 text-sm md:text-base">
                  Generate stunning videos and images for any platform with a
                  simple voice command. Perfect for Instagram, Fb, LinkedIn, and
                  more.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>AI-powered video generation</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Custom image creation</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Platform-optimized formats</span>
                  </li>
                </ul>
              </motion.div>

              {/* Feature 2 */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, margin: "-50px", amount: 0.3 }}
                transition={{
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1],
                  delay: 0.15,
                }}
                className="group bg-gradient-to-br from-[#252640] to-[#1A1B2E] p-6 md:p-8 rounded-2xl border border-white/10 hover:border-[#E5B978]/50 transition-all hover:shadow-xl hover:shadow-[#E5B978]/10"
              >
                <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-[#E5B978]/10 flex items-center justify-center mb-5 md:mb-6 group-hover:bg-[#E5B978]/20 transition-colors">
                  <Globe className="w-6 md:w-7 h-6 md:h-7 text-[#E5B978]" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-3">
                  Dynamic Digital Store Front
                </h3>
                <p className="text-white/70 mb-5 md:mb-6 text-sm md:text-base">
                  Create and update your entire website using only your voice.
                  No coding required. Make changes instantly.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Voice-based website generation</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Real-time content updates</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Mobile-responsive design</span>
                  </li>
                </ul>
              </motion.div>

              {/* Feature 3 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, margin: "-50px", amount: 0.3 }}
                transition={{
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1],
                  delay: 0.3,
                }}
                className="group bg-gradient-to-br from-[#252640] to-[#1A1B2E] p-6 md:p-8 rounded-2xl border border-white/10 hover:border-[#E5B978]/50 transition-all hover:shadow-xl hover:shadow-[#E5B978]/10 sm:col-span-2 md:col-span-1"
              >
                <div className="w-12 md:w-14 h-12 md:h-14 rounded-xl bg-[#E5B978]/10 flex items-center justify-center mb-5 md:mb-6 group-hover:bg-[#E5B978]/20 transition-colors">
                  <Phone className="w-6 md:w-7 h-6 md:h-7 text-[#E5B978]" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-3">
                  Agent That Converts
                </h3>
                <p className="text-white/70 mb-5 md:mb-6 text-sm md:text-base">
                  Let AI agents call your clients automatically. Schedule
                  follow-ups, gather feedback, and promote your work—all by
                  voice command.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Automated client calls</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Multi-language support</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#E5B978] flex-shrink-0 mt-0.5" />
                    <span>Follow-up scheduling</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section id="use-cases" className="py-14 md:py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-100px", amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-center mb-10 md:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-balance">
                Built for Every Business
              </h2>
              <p className="text-base md:text-xl text-white/70 max-w-full mx-auto">
                No matter your industry, ModalX transforms how you create
                content and connect with clients. Whether you&apos;re running a
                small business, managing a growing team, or operating a large
                enterprise, our voice-first platform adapts to your unique
                needs. Join thousands of businesses worldwide who are already
                saving time, reducing costs, and delivering better customer
                experiences with ModalX.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {/* Real Estate */}
              <UseCaseCard
                icon={<Building2 className="w-6 h-6" />}
                title="Real Estate"
                description="Create property tours, generate listing videos, and call leads automatically"
                examples={[
                  "Virtual property tours",
                  "Market update videos",
                  "Lead qualification calls",
                ]}
                index={0}
              />

              {/* Healthcare */}
              <UseCaseCard
                icon={<Stethoscope className="w-6 h-6" />}
                title="Healthcare"
                description="Share health tips, appointment reminders, and patient education content"
                examples={[
                  "Health awareness videos",
                  "Appointment confirmations",
                  "Service updates",
                ]}
                index={1}
              />

              {/* Restaurants */}
              <UseCaseCard
                icon={<UtensilsCrossed className="w-6 h-6" />}
                title="Restaurants"
                description="Showcase menu items, daily specials, and reservation management"
                examples={[
                  "Food presentation videos",
                  "Special offers",
                  "Reservation reminders",
                ]}
                index={2}
              />

              {/* Fitness */}
              <UseCaseCard
                icon={<Dumbbell className="w-6 h-6" />}
                title="Fitness & Gyms"
                description="Post workout tips, class schedules, and membership follow-ups"
                examples={[
                  "Exercise tutorials",
                  "Class promotions",
                  "Member check-ins",
                ]}
                index={3}
              />

              {/* Retail */}
              <UseCaseCard
                icon={<ShoppingBag className="w-6 h-6" />}
                title="Retail Stores"
                description="Product showcases, sale announcements, and customer outreach"
                examples={[
                  "Product demonstrations",
                  "Sale announcements",
                  "Customer feedback calls",
                ]}
                index={4}
              />

              {/* Salons */}
              <UseCaseCard
                icon={<Scissors className="w-6 h-6" />}
                title="Salons & Spas"
                description="Before/after transformations, service highlights, and booking reminders"
                examples={[
                  "Style transformations",
                  "Treatment showcases",
                  "Booking confirmations",
                ]}
                index={5}
              />

              {/* Education */}
              <UseCaseCard
                icon={<GraduationCap className="w-6 h-6" />}
                title="Education"
                description="Course content, student engagement, and parent communications"
                examples={[
                  "Educational content",
                  "Course highlights",
                  "Parent updates",
                ]}
                index={7}
              />

              {/* Many More */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: false, margin: "-50px", amount: 0.3 }}
                transition={{
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1],
                  delay: 0.64,
                }}
                className="group bg-gradient-to-br from-[#E5B978]/10 to-[#D4A84B]/5 p-4 md:p-6 rounded-xl border border-[#E5B978]/30 hover:border-[#E5B978]/60 transition-all hover:shadow-lg hover:shadow-[#E5B978]/20 flex flex-col items-center justify-center text-center h-[200px] md:h-auto"
              >
                <div className="w-10 md:w-12 h-10 md:h-12 rounded-lg bg-[#E5B978]/20 flex items-center justify-center mb-3 md:mb-4 group-hover:bg-[#E5B978]/30 transition-colors">
                  <Sparkles className="w-5 md:w-6 h-5 md:h-6 text-[#E5B978]" />
                </div>
                <h3 className="text-base md:text-lg font-bold mb-2 text-[#E5B978]">
                  And Many More
                </h3>
                <p className="text-white/70 text-xs md:text-sm">
                  Whatever your business, ModalX adapts to your needs and helps
                  you create, communicate, and grow
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section
          id="how-it-works"
          className="py-14 md:py-20 px-4 md:px-6 bg-[#1A1B2E]/50"
        >
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-100px", amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-center mb-10 md:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-balance">
                Simple as Speaking
              </h2>
              <p className="text-base md:text-xl text-white/70 max-w-full mx-auto">
                Three simple steps to transform your business communication and
                content creation forever. No complicated setup, no technical
                training, no expensive equipment needed. Just your voice, your
                ideas, and ModalX&apos;s powerful AI working together to bring
                your vision to life. Get started in minutes and see results
                immediately—it&apos;s really that simple and intuitive.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-3 gap-8 md:gap-12">
              {/* Step 1 */}
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, margin: "-50px", amount: 0.3 }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                className="text-center space-y-4 md:space-y-6"
              >
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-[#E5B978]/20 rounded-full blur-2xl" />
                  <div className="relative w-16 md:w-20 h-16 md:h-20 rounded-full bg-gradient-to-br from-[#E5B978] to-[#D4A84B] flex items-center justify-center mx-auto">
                    <span className="text-2xl md:text-3xl font-bold text-[#1A1B2E]">
                      1
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    Speak Your Intent
                  </h3>
                  <p className="text-white/70 text-sm md:text-base">
                    Tell ModalX what you want to create. &quot;Generate a video
                    about my new product&quot; or &quot;Update my website
                    homepage&quot;
                  </p>
                </div>
              </motion.div>

              {/* Step 2 */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, margin: "-50px", amount: 0.3 }}
                transition={{
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1],
                  delay: 0.15,
                }}
                className="text-center space-y-4 md:space-y-6"
              >
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-[#E5B978]/20 rounded-full blur-2xl" />
                  <div className="relative w-16 md:w-20 h-16 md:h-20 rounded-full bg-gradient-to-br from-[#E5B978] to-[#D4A84B] flex items-center justify-center mx-auto">
                    <span className="text-2xl md:text-3xl font-bold text-[#1A1B2E]">
                      2
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    AI Creates
                  </h3>
                  <p className="text-white/70 text-sm md:text-base">
                    Our AI instantly understands your request and generates
                    professional content, websites, or schedules calls
                  </p>
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div
                initial={{ opacity: 0, x: 100 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, margin: "-50px", amount: 0.3 }}
                transition={{
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1],
                  delay: 0.3,
                }}
                className="text-center space-y-4 md:space-y-6"
              >
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-[#E5B978]/20 rounded-full blur-2xl" />
                  <div className="relative w-16 md:w-20 h-16 md:h-20 rounded-full bg-gradient-to-br from-[#E5B978] to-[#D4A84B] flex items-center justify-center mx-auto">
                    <span className="text-2xl md:text-3xl font-bold text-[#1A1B2E]">
                      3
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3">
                    Review & Deploy
                  </h3>
                  <p className="text-white/70 text-sm md:text-base">
                    Review the AI-generated content, make any adjustments with
                    voice commands, and deploy instantly
                  </p>
                </div>
              </motion.div>
            </div>

            {/* CTA */}
            <div className="text-center items-center mt-12 md:mt-16">
              <Box
                onClick={() => navigate("/workspace")}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 16px",
                  gap: "8px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.20)",
                  background: "#F6BE6B",
                  boxShadow: "0px 0px 24px 0px rgba(253, 215, 157, 0.60)",
                  color: "#17182C",
                  textAlign: "center",
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  maxWidth: isBelow991 ? "350px" : "386px",
                  margin: "0 auto",
                  cursor: "pointer",
                  transition: "background 0.3s ease",
                  width: "100%",
                }}
                sx={{
                  "@media (min-width: 640px)": {
                    width: "auto",
                  },
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#E5AD5A"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#F6BE6B"}
              >
                <span>Try ModalX Now</span>
                <Mic style={{ width: "20px", height: "20px", marginLeft: "8px" }} />
              </Box>
            </div>
          </div>
        </section>

        {/* Global Reach Section */}
        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, margin: "-100px", amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              className="text-center mb-10 md:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-balance">
                Trusted Globally
              </h2>
              <p className="text-base md:text-xl text-white/70 max-w-full mx-auto">
                Businesses around the world are transforming with ModalX, from
                bustling startups in Mumbai to established enterprises in
                Toronto and innovative teams across the United States. Join a
                global community of forward-thinking businesses who have
                discovered the power of voice-first AI. Our platform supports
                multiple languages and adapts to local business needs, making it
                the perfect choice for companies of any size, anywhere in the
                world.
              </p>
            </motion.div>

            {/* World Map Container */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: false, margin: "-100px", amount: 0.3 }}
              transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative bg-gradient-to-br from-[#252640] to-[#1A1B2E] p-4 md:p-8 lg:p-12 rounded-2xl md:rounded-3xl border border-white/10 overflow-hidden"
            >
              <InteractiveWorldMap />
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}
